import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Divider
} from "antd";
import {
  UnlockOutlined
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ApiResponse } from "../../types/api.types";
import dayjs from "dayjs";
import { PERMISSIONS } from "../../constants/permissions";
import api from "../../services/api";

const { Title, Text } = Typography;

// Define types
interface BlockedUser {
  id: number;
  username: string;
  email: string;
  failedLoginAttempts: number;
  lockedUntil: string;
  lastFailedLoginAt: string;
}

interface BlockedUserResponse {
  content: BlockedUser[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

// Helper function for formatting dates
const formatDateTime = (
  dateString: string | null | undefined,
  format = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!dateString) return 'N/A';
  const date = dayjs(dateString);
  return date.isValid() ? date.format(format) : 'N/A';
};

const BlockedUsersList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<BlockedUser[]>([]);
  const { permissions } = useSelector((state: RootState) => state.auth);

  const fetchBlockedUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get<ApiResponse<BlockedUserResponse>>('/api/v1/user-block/list');
      setData(response.data.data.content || []);
    } catch (error) {
      console.error("Error fetching blocked users:", error);
      message.error("Failed to fetch blocked users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const handleUnblockUser = async (username: string) => {
    try {
      await api.post('/api/v1/user-block/unblock', { id: username });
      message.success(`User ${username} has been unblocked successfully`);
      fetchBlockedUsers();
    } catch (error) {
      console.error("Error unblocking user:", error);
      message.error("Failed to unblock user");
    }
  };

  const hasPermission = (permissionKey: string) => {
    return permissions.some((p: any) => p.name === permissionKey);
  };

  const columns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      render: (text: string) => text || "N/A",
    },
    {
      title: "Failed Login Attempts",
      dataIndex: "failedLoginAttempts",
      key: "failedLoginAttempts",
      width: 150,
    },
    {
      title: "Locked Until",
      dataIndex: "lockedUntil",
      key: "lockedUntil",
      width: 180,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Last Failed Login",
      dataIndex: "lastFailedLoginAt",
      key: "lastFailedLoginAt",
      width: 180,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (record: BlockedUser) => (
        hasPermission(PERMISSIONS.UNBLOCK_USER?.name) ? (
          <Popconfirm
            title="Are you sure you want to unblock this user?"
            onConfirm={() => handleUnblockUser(record.username)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              type="primary"
              size="small"
              icon={<UnlockOutlined />} 
            >
              Unblock
            </Button>
          </Popconfirm>
        ) : null
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>Blocked User Accounts</Title>
            <Text type="secondary">View and manage blocked user accounts (separate from IP blocking)</Text>
          </div>
        </div>
        <Divider style={{ margin: '16px 0' }} />
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          loading={loading}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default BlockedUsersList; 